<template>
  <form
    autocomplete="off"
    @submit.prevent="$emit('submit')"
  >
    <div
      v-if="showWarning"
      class="light-warning-banner"
    >
      <strong class="block">Attention: Replacing Existing File</strong>
      You are about to overwrite a previously uploaded PDF. Please ensure the new file is correct and matches the selected Annexure Type before proceeding.
    </div>

    <fieldset class="!w-full">
      <label :class="{ 'text-danger' : errors?.type}">Annexure type
        <span class="text-danger">*</span>
      </label>

      <AppDropdown
        :trigger-text="selectedAnnexureType?.name"
        :icon="faCaretDown"
        :class="{ 'border border-danger rounded !mb-0': errors?.type}"
        class="[&_.dropdown-content]:w-[495px] [&_.dropdown-trigger]:!py-1.5 text-sm mb-5"
      >
        <template v-if="!loading">
          <AppDropdownItem
            v-for="annexureType in planStore.annexureTypes"
            :key="annexureType.id"
            class="!py-1.5 border-none"
            :class="{ 'active': model.type === annexureType.id }"
            @click="changeAnnexureType(annexureType)"
          >
            {{ annexureType.name }}
          </AppDropdownItem>
        </template>
        <AppDropdownItem v-else>
          Loading Annexure Types...
        </AppDropdownItem>
      </AppDropdown>

      <span
        v-if="errors?.type"
        class="text-xs text-danger"
      >
        {{ errors?.type }}
      </span>

      <TextInput
        v-if="isAdditionalType"
        v-model.trim="model.name"
        v-focus
        required
        :validation-error="errors?.name"
        label="Annexure name"
      />

      <label
        class="mb-0"
        :class="{ 'text-danger mt-2' : errors?.document}"
      >Upload a file
        <span class="text-danger">*</span>
      </label>
      <div
        class="mb-5 pt-1 relative flex items-stretch [&_.btn]:py-2 [&_.btn]:px-4 w-full group"
        :class="{ '!mb-1' : errors?.document}"
      >
        <FileUploadButton
          text="Choose file"
          class="btn-solid btn-primary whitespace-nowrap"
          accept=".pdf"
          @file-selected="uploadFile($event)"
        />

        <FileUploadButton
          :icon="model.file?.name ? faFilePdf : null"
          :text="model.file?.name"
          class="border border-l-0 text-secondary flex-1 !px-3 group-hover:border-chrome [&>div]:w-full leading-snug text-start"
          accept=".pdf"
          @file-selected="uploadFile($event)"
        />
      </div>

      <span
        v-if="errors?.document"
        class="text-xs text-danger block mb-5"
      >
        {{ errors?.document }}
      </span>
    </fieldset>
  </form>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';

import { FileUploadButton } from '@/components/buttons';
import { AppDropdown, AppDropdownItem } from '@/components/dropdowns';
import { TextInput } from '@/components/form-inputs';

import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { faCaretDown } from '@fortawesome/sharp-solid-svg-icons';

import { usePlanStore } from '@/store';

defineEmits(['submit']);

defineProps({
  isEditing: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: Object,
    default: () => null,
  },
});

const model = defineModel();

const planStore = usePlanStore();
const loading = ref(true);

onMounted(async () => {
  loading.value = true;
  await planStore.loadAnnexureTypes();
  loading.value = false;
});

const selectedAnnexureType = computed(() => {
  return planStore.annexureTypes.find(annexureType => annexureType.id === model.value.type);
});

// Check if the selected type is not required (i.e. it is additional)
const isAdditionalType = computed(() => {
  return selectedAnnexureType.value ? !selectedAnnexureType.value.required : false;
});

// Computed property to determine if the warning should be shown
const showWarning = computed(() => {
  // Must be a Required Type
  if (isAdditionalType.value) return false;

  // Check if a document of this Type is already uploaded in the Plan
  // If we are editing the existing one, we don't warn unless we are changing types,
  // so check if there is a different annexure with this ID.
  return planStore.planAnnexures.some(annexure =>
    annexure.type?.id === model.value.type && annexure.id !== model.value.id,
  );
});

const changeAnnexureType = (type) => {
  model.value.type = type.id;

  if (!type.required) {
    if (!model.value.name) {
      model.value.name = '';
    }
  } else {
    model.value.name = null;
  }
};

const uploadFile = (file) => {
  if (!file) {
    model.value.file = { name: '', href: '#' };
    return;
  }
  if (file instanceof File) {
    model.value.file = {
      name: file.name,
      href: URL.createObjectURL(file),
      isLocal: true,
      rawFile: file,
    };
  } else if (typeof file === 'object') {
    model.value.file = {
      name: file.name || 'Unnamed Document',
      href: file.url || file.href,
      isLocal: false,
    };
  }
};

</script>
